export enum ObjectStorageProvider {
  S3 = 'S3',
  GCS = 'GCS',
  AWS = 'AWS',
  Azure = 'Azure'
}

export enum ObjectStorageAuthType {
  AccessKey = 'AccessKey',
  ServiceAccount = 'ServiceAccount',
  OAuth = 'OAuth',
  /** Keyless: Enlighten's own service account, through an approved storage grant (EHQ-427). GCS only. */
  ServerIdentity = 'ServerIdentity'
}

export interface ObjectStorageConnection {
  connectionId?: number;
  /** The project that owns this connection (numeric project ID) */
  projectId?: number;
  name: string;
  description?: string | null;
  provider: ObjectStorageProvider;
  authType: ObjectStorageAuthType;
  bucket: string;
  /** Key prefix inside the bucket (`''` or `'segment/'`). */
  prefix?: string | null;
  /** The storage grant a ServerIdentity connection uses (EHQ-427). */
  storageGrantId?: number | null;
  endpoint?: string;
  region?: string;
  /**
   * GCS project ID string.
   * The server serializes this field as "projectId" (not "gcsProjectId").
   * Use gcsProjectId in client code; map to/from projectId when communicating with the server.
   */
  gcsProjectId?: string;
  accountName?: string;
  accessKey?: string;
  secretId?: number;
  userCredentialId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateObjectStorageRequest {
  name: string;
  /** Always send a string (use `''` when empty) when the API expects a non-null description. */
  description: string;
  provider: ObjectStorageProvider;
  authType: ObjectStorageAuthType;
  bucket: string;
  endpoint?: string;
  region?: string;
  projectId?: string;
  accountName?: string;
  accessKey?: string;
  secretId?: number;
  userCredentialId?: number;
  prefix?: string;
  /** Required with ServerIdentity, and only allowed there. */
  storageGrantId?: number;
}

export interface UpdateObjectStorageRequest {
  name?: string;
  description?: string;
  provider?: ObjectStorageProvider;
  authType?: ObjectStorageAuthType;
  bucket?: string;
  endpoint?: string;
  region?: string;
  projectId?: string;
  accountName?: string;
  accessKey?: string;
  secretId?: number;
  userCredentialId?: number;
  prefix?: string;
  /** Required with ServerIdentity, and only allowed there. */
  storageGrantId?: number;
}

/**
 * Request payload for testing an object storage connection before saving.
 * Sent to POST /api/project/{id}/object-storage-test
 */
export interface TestObjectStorageRequest {
  // Required by the server: the test endpoint deserializes the body with the
  // same (create) request shape, which mandates `name`. The value is ignored
  // for testing but must be present, or the request fails to deserialize
  // (`missing field \`name\``) before credentials are ever checked.
  name: string;
  provider: ObjectStorageProvider;
  authType: ObjectStorageAuthType;
  bucket: string;
  endpoint?: string;
  region?: string;
  projectId?: string;
  accountName?: string;
  accessKey?: string;
  secretId?: number;
  prefix?: string;
  /** Required with ServerIdentity, and only allowed there. */
  storageGrantId?: number;
}

/**
 * Result of testing an object storage connection.
 * Matches server's TestConnectionResponse struct.
 */
export interface ObjectStorageTestResult {
  success: boolean;
  /** Error message if the test failed. */
  error?: string | null;
  /** Error type for client-side handling (e.g. "UnsupportedProvider", "UnsupportedAuthType"). */
  errorKind?: string | null;
}
